import { tap, omit } from 'lodash/fp';
import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';

import { useModalProviderContext } from '../ModalProvider';
import { installationSummaryModal } from '../ModalProvider/modalNames';
import CloseIcon from '../../themes/svg/closeIcon';
import cartAsMandiJob from '../../services/cartAsMandiJob';
import { REASON_CODE_OPTIONS } from '../InstallationModalCommon/JobOptions/JobOptions';

import {
  ResponsiveInstallationSummaryModal,
  FlexModal,
  FlexJobOptions,
  FlexAddToCartButton,
  FlexDesignSummary,
  ModalTitle,
  FlexAddToCartButtonContainer,
  CloseModalButton,
  FlexProjectDetails,
} from './styles';
import useInstallationSummaryState from './useInstallationSummaryState';

const InstallationSummaryModal = ({
  designId,
  selectedProducts,
  selectedLeafItems,
  updateInstallationPricing,
  selectedProductOnly,
  setLoading,
  textOptInNumber,
  globalError,
  haulAwayCount,
  removalCount,
  adjustmentCount,
}) => {
  const { isOpen, setIsOpen } = useModalProviderContext(installationSummaryModal);
  const allProductsCount = selectedLeafItems.length;

  const wrappedUpdateInstallationPricing = useCallback(
    (id, leafItems, opts, mode) => {
      const pricingOpts = omit(['isReinstall', 'reasonCode'], opts);
      if (opts.isReinstall && opts.reasonCode) {
        const selected = REASON_CODE_OPTIONS.find(o => o.value === opts.reasonCode);
        if (selected) pricingOpts.reinstall = selected.code;
      }
      return updateInstallationPricing(id, leafItems, pricingOpts, mode);
    },
    [updateInstallationPricing],
  );

  const [options, setOption, setOptions] = useInstallationSummaryState(
    isOpen,
    designId,
    selectedLeafItems,
    wrappedUpdateInstallationPricing,
    {
      windowHaul: !!haulAwayCount,
      windowRemoval: !!removalCount,
      windowAdjustment: !!adjustmentCount,
      windowHaulCount: haulAwayCount || allProductsCount,
      windowRemoveCount: removalCount || allProductsCount,
      windowAdjustmentCount: adjustmentCount || allProductsCount,
      textOptIn: true,
      textOptInNumber,
      specialInstructions: '',
      isReinstall: false,
      reasonCode: 0,
    },
    ['windowHaul', 'windowRemoval', 'windowAdjustment', 'windowHaulCount', 'windowRemoveCount', 'windowAdjustmentCount', 'isReinstall', 'reasonCode'],
  );

  useEffect(() => {
    setOptions(prev => ({
      ...prev,
      windowHaul: !!haulAwayCount,
      windowRemoval: !!removalCount,
      windowAdjustment: !!adjustmentCount,
      windowHaulCount: haulAwayCount || allProductsCount,
      windowRemoveCount: removalCount || allProductsCount,
      windowAdjustmentCount: adjustmentCount || allProductsCount,
    }));
  }, [haulAwayCount, removalCount, adjustmentCount, allProductsCount, setOptions]);

  const addToCart = useCallback(() => {
    setLoading(true);

    const submitOptions = { ...options };
    if (submitOptions.isReinstall && submitOptions.reasonCode) {
      const selected = REASON_CODE_OPTIONS.find(o => o.value === submitOptions.reasonCode);
      if (selected) {
        submitOptions.reinstall = selected.code;
        submitOptions.reworkType = 'reinstall';
      }
    }
    delete submitOptions.isReinstall;
    delete submitOptions.reasonCode;

    cartAsMandiJob({
      designId,
      selectedProducts,
      leafItems: selectedLeafItems,
      options: {
        createAndSaveJob: submitOptions,
      },
      productOnlyItems: selectedProductOnly,
    })
      .then(tap(() => setLoading(false)))
      .catch(globalError);
  }, [designId, selectedProducts, options, setLoading, globalError, selectedLeafItems]);

  const isDisabled = ({
    windowHaul,
    windowRemoval,
    windowAdjustment,
    windowHaulCount,
    windowRemoveCount,
    windowAdjustmentCount,
    isReinstall,
    reasonCode,
  }) => {
    if (windowHaul === true && parseInt(windowHaulCount) === 0) {
      return true;
    }
    if (windowRemoval === true && parseInt(windowRemoveCount) === 0) {
      return true;
    }
    if (windowAdjustment === true && parseInt(windowAdjustmentCount) === 0) {
      return true;
    }
    if (isReinstall && !reasonCode) {
      return true;
    }
    return false;
  };

  return (
    <ResponsiveInstallationSummaryModal
      id="summaryModal"
      isOpen={isOpen}
      onRequestClose={() => setIsOpen(false)}
      style={{
        overlay: {
          zIndex: 100,
          backgroundColor: 'rgba(0, 0, 0, .8)',
        },
      }}
    >
      <ModalTitle>
        Installation Summary
        <CloseModalButton
          id="closeModalButton"
          title="close"
          onClick={() => setIsOpen(false)}
          tabIndex="-1"
          role="button"
        >
          <CloseIcon />
        </CloseModalButton>
      </ModalTitle>
      <FlexModal id="flexModal">
        <FlexProjectDetails>
          <FlexJobOptions
            id="jobOptionsPanel"
            {...options}
            onOptionChange={setOption}
            totalWindowCount={allProductsCount}
            windowAdjustmentCount={options.windowAdjustmentCount}
            windowHaulCount={options.windowHaulCount}
            windowRemoveCount={options.windowRemoveCount}
            windowAdjustment={options.windowAdjustment}
            windowHaul={options.windowHaul}
            windowRemoval={options.windowRemoval}
          />
          <FlexAddToCartButtonContainer>
            <FlexAddToCartButton id="addToCartButton" onClick={addToCart} disabled={isDisabled(options)} />
          </FlexAddToCartButtonContainer>
        </FlexProjectDetails>

        <FlexDesignSummary id="designSummaryPanel" designId={designId} />
      </FlexModal>
    </ResponsiveInstallationSummaryModal>
  );
};

InstallationSummaryModal.propTypes = {
  designId: PropTypes.string.isRequired,
  selectedProducts: PropTypes.arrayOf(PropTypes.shape).isRequired,
  selectedLeafItems: PropTypes.arrayOf(PropTypes.string).isRequired,
  updateInstallationPricing: PropTypes.func.isRequired,
  setLoading: PropTypes.func.isRequired,
  selectedProductOnly: PropTypes.arrayOf(PropTypes.shape).isRequired,
  globalError: PropTypes.func.isRequired,
  textOptInNumber: PropTypes.string.isRequired,
  haulAwayCount: PropTypes.number.isRequired,
  removalCount: PropTypes.number.isRequired,
  adjustmentCount: PropTypes.number.isRequired,
};
export default InstallationSummaryModal;
